import { TablePagination, type TablePaginationProps } from '@mui/material'
import { styled } from '../../../utils/styled'
import Grid from '../../Grid'
import { TOTAL_ROWS_COUNT_CLASS_NAME } from '../classNames'
import { TABLE_PAGINATION_HEIGHT } from './constants'

type StyledTablePaginationProps = TablePaginationProps & {
  inScrollingContainer: boolean
}

export const StyledTablePagination = styled(TablePagination, {
  shouldForwardProp: (prop) => prop !== 'inScrollingContainer'
})<StyledTablePaginationProps>(({ theme, inScrollingContainer }) => ({
  width: inScrollingContainer ? '100cqw' : '100%',
  ...(inScrollingContainer ? { position: 'sticky', left: 0, bottom: 0 } : {}),
  borderBottom: 'none',
  '.MuiToolbar-root': {
    marginTop: '24px',
    padding: 0
  },
  '.MuiTablePagination-toolbar': {
    justifyContent: 'space-between',
    height: `${TABLE_PAGINATION_HEIGHT}px`,
    minHeight: `${TABLE_PAGINATION_HEIGHT}px`
  },
  '.MuiTablePagination-spacer': {
    display: 'none'
  },
  '.MuiInputBase-root': {
    position: 'absolute',
    left: 0,
    // Sticky pagination (inScrollingContainer) makes this cell the containing block for
    // absolute children. Without an intrinsic width, MUI's select `width: 100%` can stretch
    // the control across the container and overlap the "Rows per page" label.
    boxSizing: 'border-box',
    height: `${TABLE_PAGINATION_HEIGHT}px`,
    width: 'fit-content',
    minWidth: '48px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    border: `1px solid ${theme.tokens.border.main.tonal}`,
    borderRadius: theme.borderRadius.medium,
    margin: 0,
    svg: {
      display: 'none'
    },
    input: {
      height: '100%'
    },
    // Override MUI Select icon padding; keep modest horizontal inset so "10"/"50" aren't cramped.
    '.MuiSelect-select.MuiInputBase-input': {
      boxSizing: 'border-box',
      height: '100%',
      minWidth: '48px',
      paddingTop: 0,
      paddingBottom: 0,
      paddingLeft: theme.spacing(2),
      paddingRight: theme.spacing(2),
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      lineHeight: 1,
      textAlign: 'center',
      '&:focus': {
        backgroundColor: 'transparent'
      }
    }
  },
  '.MuiTablePagination-selectLabel': {
    color: theme.tokens.foreground.main.secondary,
    position: 'absolute',
    // ~48px control + gap; spacing(9)=64px.
    left: theme.spacing(9),
    margin: 0
  },
  '.MuiTablePagination-displayedRows': {
    position: 'absolute',
    left: '50%',
    transform: 'translateX(-50%)',
    margin: 0
  },
  [`.${TOTAL_ROWS_COUNT_CLASS_NAME}`]: {
    color: theme.tokens.foreground.main.secondary
  }
}))
export const StyledPaginationActions = styled(Grid)(({ theme }) => ({
  position: 'absolute',
  alignItems: 'center',
  right: '0',
  border: `1px solid ${theme.tokens.border.main.tonal}`,
  borderRadius: theme.borderRadius.medium,
  display: 'flex',
  gap: '6px',
  height: `${TABLE_PAGINATION_HEIGHT}px`,
  overflowY: 'hidden'
}))
export const StyledPageIndex = styled(Grid)(({ theme }) => ({
  alignItems: 'center',
  border: `1px solid ${theme.tokens.border.main.tonal}`,
  borderRadius: theme.borderRadius.medium,
  justifyContent: 'center',
  padding: '8px 24px',
  display: 'flex',
  width: '36px',
  height: `${TABLE_PAGINATION_HEIGHT}px`
}))
